import { isArray, isObject } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { LabelValueItem } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { cloneElement, useState } from 'react';
import { IphoneBottomAdaptive } from '../iphone-bottom-adaptive';
import { ModalDrawer } from '../modal-drawer';
import { NativeScroll } from '../native-scroll';
import { Spinner } from '../spinner';
import { Tabs } from '../tabs';
import './style.less';

export interface TabSelectItem {
  label: string;
  value: string | number;
}

export type TabSelectChangeValueParam = { label: string; value: TabSelectItem[] };

export interface TabSelectProps {
  className?: string;
  title: string | JSX.Element;
  visible?: boolean;
  onClose?: () => void;
  getDataSource: (
    code: TabSelectItem['value'] | undefined,
    selectedItem: TabSelectItem | undefined,
    nextLevel: number,
  ) => Promise<TabSelectItem[]>;
  onChange?: (selectedData?: TabSelectChangeValueParam) => void;
  levelNumber?: number;
  // 配合Form表单组合使用，定义的格式
  value?: Array<TabSelectItem['value']> | TabSelectItem[];
  // 自定义loading，组件会传递属性spinning来控制loading显示隐藏
  loading?: JSX.Element | false;
  onValueChange?: (selectedData?: LabelValueItem[]) => void;
}

const useValue = (value: TabSelectItem['value'][] | TabSelectItem[]) => {
  value = isArray(value) ? value : [];
  return value.map((item) => {
    return (isObject(item) ? item.value : item) as TabSelectItem['value'];
  });
};

/**
 * 多级选择
 * ```
 * 1. 与Form结合使用赋值与取值方式
 *    例如：
 *    a. 赋值格式: { label: '山西省太原市万柏林区', value: ['140000', '140100', '140106'] }
 *    b. 取值格式:
 *        {
 *          label: '天津市天津市河东区',
 *          value: [{label: '天津市', value: '120000'},
 *                  {label: '天津市', value: '120100'},
 *                  {label: '河东区', value: '120102'}]
 *        }
 *       可通过使用form.getFormModalPickerValue(value) 直接取值 => ['120000', '120100', '120102']
 * ```
 */
export const TabSelect = (props: TabSelectProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [tabItemList, setTabItemList] = useState<TabSelectItem[][]>([]);
  const [selectedList, setSelectedList] = useState<TabSelectItem[]>([]);
  const [loading, setLoading] = useState(false);
  const value = useValue(props.value || []);

  const onChangeTab = (currentIndex) => {
    setActiveIndex(Number(currentIndex));
  };

  const onRequestFirstLevel = hooks.useCallbackRef(async () => {
    try {
      const dataList = await props.getDataSource(undefined, undefined, 1);
      setTabItemList([dataList]);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  });

  hooks.useDeepCompareEffect(() => {
    setLoading(true);
    if (value.length > 0) {
      void valuePropHandle();
    } else {
      props.onValueChange?.(undefined);
      void onRequestFirstLevel();
    }
  }, [value]);

  const valuePropHandle = hooks.useCallbackRef(async () => {
    try {
      // 验证当前缓存数据是否满足要求
      let validate = true;
      let selectedList = [] as TabSelectItem[];
      if (!tabItemList || tabItemList.length !== value.length) {
        validate = false;
      } else {
        for (let index = 0; index < value.length; index++) {
          const code = value[index];
          const target = tabItemList[index].find((item) => item.value === code);
          if (!target) {
            validate = false;
          } else {
            selectedList.push(target);
          }
        }
      }
      if (validate) {
        setSelectedList(selectedList);
        props.onValueChange?.(selectedList);
        setLoading(false);
        return;
      }
      const promiseAll = [props.getDataSource(undefined, undefined, 1)];
      value.forEach((code, index) => {
        if (index === value.length - 1) {
          return;
        }
        promiseAll.push(props.getDataSource(code, undefined, index + 1 + 1));
      });
      const allDataList = await Promise.all(promiseAll);
      selectedList = [] as TabSelectItem[];
      for (let index = 0; index < value.length; index++) {
        const code = value[index];
        const target = allDataList[index].find((item) => item.value === code);
        if (target) {
          selectedList.push(target);
        } else {
          props.onChange?.(undefined);
          throw new Error('参数value提供的数据级联关系异常');
        }
      }
      setTabItemList(allDataList);
      setSelectedList(selectedList);
      onChangeTab(selectedList.length - 1);
      setLoading(false);
      onFinish(selectedList);
    } catch (error) {
      console.error(error);
      void onRequestFirstLevel();
    }
  });

  const onFinish = hooks.useCallbackRef((selectedList: TabSelectItem[]) => {
    const labelList = selectedList.map((item) => {
      return item.label;
    });
    // 此处value赋值需要将引用关系打断
    props.onChange?.({ label: labelList.join(''), value: [...selectedList] });
    props.onValueChange?.(selectedList);
  });

  const onClickSelectItem = (tabIndex: number, childItem: TabSelectItem) => {
    selectedList[tabIndex] = childItem;
    const newTabItemList = tabItemList.slice(0, tabIndex + 1);
    const newSelectedList = selectedList.slice(0, tabIndex + 1);
    setTabItemList(newTabItemList);
    setSelectedList(newSelectedList);

    if (props.levelNumber && props.levelNumber > 0 && newSelectedList.length === props.levelNumber) {
      setTimeout(() => {
        onFinish(newSelectedList);
      }, 100);
      return;
    }
    setLoading(true);
    void props
      .getDataSource(childItem.value, childItem, tabIndex + 2)
      .then((list) => {
        setLoading(false);
        if (list.length > 0) {
          newTabItemList[tabIndex + 1] = list;
          setTabItemList(newTabItemList);
          setTimeout(() => {
            onChangeTab(tabIndex + 1);
          }, 100);
        } else {
          onFinish(newSelectedList);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <ModalDrawer
      className={classNames('v-tab-select', props.className)}
      visible={props.visible || false}
      closeIcon={true}
      onClose={props.onClose}
      position="bottom"
      title={props.title}
    >
      <div className="tab-select-content">
        {props.loading !== false &&
          (props.loading ? (
            cloneElement(props.loading, { spinning: loading })
          ) : (
            <Spinner spinning={loading} />
          ))}
        <Tabs
          height={800}
          activeKey={`${activeIndex}`}
          onChange={onChangeTab}
          position="top"
          tabsType="fixed"
          hideActiveLine={true}
        >
          {tabItemList.map((item, index) => {
            return (
              <Tabs.Pane title={selectedList?.[index]?.label || '请选择'} key={index}>
                <NativeScroll className="ts-list">
                  {item.map((childItem) => {
                    const isActive = childItem.value === selectedList[index]?.value;
                    const className = classNames('tsl-item', { 'tsl-item-active': isActive });
                    return (
                      <div
                        className={className}
                        key={childItem.value}
                        onClick={onClickSelectItem.bind(null, index, childItem)}
                      >
                        {childItem.label}
                        {isActive && <span className="tsl-active-icon" />}
                      </div>
                    );
                  })}
                </NativeScroll>
              </Tabs.Pane>
            );
          })}
        </Tabs>
      </div>
      <IphoneBottomAdaptive />
    </ModalDrawer>
  );
};

TabSelect['domTypeName'] = 'TabSelect';
